import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { stampProof } from "@/lib/stampProof";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Truck, MapPin, Phone, Play, KeyRound, CheckCircle2, Loader2, RefreshCw, Package,
  Camera, X,
} from "lucide-react";

const STATUS_META = {
  confirmed:     { label: "Ready to start", color: "bg-blue-50 text-blue-700",   dot: "bg-blue-500" },
  dispatched:    { label: "Out for delivery", color: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  awaiting_otp:  { label: "Awaiting OTP",   color: "bg-fuchsia-50 text-fuchsia-700", dot: "bg-fuchsia-500" },
};

export default function EmployeeDeliveries() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [otpOrder, setOtpOrder] = useState(null); // { order, entered }
  const [otp, setOtp] = useState("");
  const [proofPhoto, setProofPhoto] = useState("");
  const [proofCompressing, setProofCompressing] = useState(false);
  const cameraInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/employee/deliveries");
      setOrders(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load deliveries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Broadcast the employee's live location while any delivery is in-flight.
  // Runs at most every 30s and stops when there are no dispatched / awaiting
  // orders, so we don't drain battery unnecessarily.
  useEffect(() => {
    const activeDelivery = orders.some(
      (o) => o.status === "dispatched" || o.status === "awaiting_otp",
    );
    if (!activeDelivery || !navigator.geolocation) return undefined;
    let cancelled = false;
    const push = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          api.post("/employee/location", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 20_000 },
      );
    };
    push();
    const id = window.setInterval(push, 30_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [orders]);

  const start = async (o) => {
    setBusyId(o.id);
    try {
      await api.post(`/employee/deliveries/${o.id}/start`);
      toast.success("Delivery started");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start delivery");
    } finally { setBusyId(null); }
  };

  const arrive = async (o) => {
    setBusyId(o.id);
    try {
      await api.post(`/employee/deliveries/${o.id}/arrive`);
      toast.success("OTP generated — ask the customer to read it out from their app");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate OTP");
    } finally { setBusyId(null); }
  };

  const openVerify = (o) => {
    setOtp("");
    setProofPhoto("");
    setOtpOrder(o);
  };

  const capturePhoto = async (file) => {
    if (!file) return;
    if (!otpOrder) return;
    setProofCompressing(true);
    try {
      const { dataUrl } = await compressImage(file, { maxDimension: 1200, quality: 0.75 });
      const stamped = await stampProof(dataUrl, {
        timestamp: new Date().toISOString(),
        pin: otpOrder.pin_code || "",
        orderId: otpOrder.id,
      });
      setProofPhoto(stamped);
    } catch {
      toast.error("Could not read that photo. Try again.");
    } finally {
      setProofCompressing(false);
    }
  };

  const verifyOtp = async () => {
    const clean = otp.replace(/\D/g, "");
    if (clean.length !== 4) {
      toast.error("OTP must be 4 digits.");
      return;
    }
    if (!proofPhoto) {
      toast.error("Snap a doorstep photo first — it's required.");
      return;
    }
    setBusyId(otpOrder.id);
    try {
      await api.post(`/employee/deliveries/${otpOrder.id}/verify-otp`, {
        otp: clean,
        proof_photo: proofPhoto,
      });
      toast.success("Delivered! 🎉");
      setOtpOrder(null);
      setProofPhoto("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong OTP");
    } finally { setBusyId(null); }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1100px]" data-testid="employee-deliveries">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">Deliveries</div>
          <h1 className="font-heading text-3xl font-bold mt-1 flex items-center gap-2">
            <Truck className="w-7 h-7" /> My deliveries
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Orders assigned to you. Tap <b>Start</b> when you leave the shop, <b>Arrive</b> at the customer&apos;s door to generate an OTP, then confirm the code the customer reads out.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="deliveries-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : orders.length === 0 ? (
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-8 text-center text-slate-500">
            <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="font-medium">No deliveries yet</div>
            <div className="mono text-[11px] mt-1">Assignments will show up here after the owner confirms an order for your PIN.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {orders.map((o) => {
            const meta = STATUS_META[o.status] || { label: o.status, color: "bg-slate-100 text-slate-700", dot: "bg-slate-400" };
            return (
              <Card key={o.id} className="border-slate-200 shadow-none" data-testid={`delivery-card-${o.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="mono text-[11px] text-slate-500">#{o.id.slice(0, 8)}</div>
                      <div className="font-medium text-sm">{o.customer_name || "—"}</div>
                    </div>
                    <span className={"mono text-[10px] px-1.5 py-0.5 rounded uppercase flex items-center gap-1 " + meta.color}>
                      <span className={"w-1.5 h-1.5 rounded-full " + meta.dot} />
                      {meta.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mono text-slate-600 mb-3">
                    <div className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {o.delivery_address || "—"} · {o.pin_code}</div>
                    <div className="flex items-start gap-1.5"><Phone className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {o.customer_mobile || "—"}</div>
                  </div>
                  <div className="mono text-[11px] text-slate-500 border-t border-slate-100 pt-2 mb-3">
                    {(o.items || []).slice(0, 3).map((i) => `${i.quantity}× ${i.name}`).join(" · ")}
                    {o.items && o.items.length > 3 ? ` · +${o.items.length - 3} more` : ""}
                    <b className="ml-2 text-slate-800">Total ₹{Number(o.total || 0).toLocaleString("en-IN")}</b>
                    {o.payment_status === "paid" && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 uppercase text-[9px]">Prepaid</span>
                    )}
                    {o.payment_method === "cod" && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 uppercase text-[9px]">Collect ₹{Number(o.total || 0).toLocaleString("en-IN")} (COD)</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {o.status === "confirmed" && (
                      <Button
                        size="sm"
                        onClick={() => start(o)}
                        disabled={busyId === o.id}
                        className="bg-blue-600 hover:bg-blue-700 h-8"
                        data-testid={`delivery-start-${o.id}`}
                      >
                        <Play className="w-3.5 h-3.5 mr-1" /> Start delivery
                      </Button>
                    )}
                    {o.status === "dispatched" && (
                      <Button
                        size="sm"
                        onClick={() => arrive(o)}
                        disabled={busyId === o.id}
                        className="bg-fuchsia-600 hover:bg-fuchsia-700 h-8"
                        data-testid={`delivery-arrive-${o.id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1" /> Arrived — send OTP
                      </Button>
                    )}
                    {o.status === "awaiting_otp" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => openVerify(o)}
                          disabled={busyId === o.id}
                          className="bg-emerald-600 hover:bg-emerald-700 h-8"
                          data-testid={`delivery-verify-${o.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Enter OTP to close
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => arrive(o)}
                          disabled={busyId === o.id}
                          className="h-8"
                          title="Regenerate OTP if the customer didn't get one"
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Resend OTP
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={!!otpOrder}
        onOpenChange={(v) => { if (!v) { setOtpOrder(null); setProofPhoto(""); } }}
      >
        <DialogContent className="max-w-sm" data-testid="delivery-otp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-fuchsia-600" /> Confirm delivery
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Ask the customer to open their <b>Order history</b> page and read out the 4-digit code. Snap a doorstep photo before submitting.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {/* Doorstep proof photo — required */}
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span>Doorstep photo <span className="text-red-500">*</span></span>
                {proofPhoto && (
                  <button
                    type="button"
                    onClick={() => setProofPhoto("")}
                    className="text-[11px] text-slate-500 hover:text-red-600 flex items-center gap-1"
                    data-testid="delivery-photo-clear"
                  >
                    <X className="w-3 h-3" /> Retake
                  </button>
                )}
              </Label>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => capturePhoto(e.target.files?.[0])}
                data-testid="delivery-photo-input"
              />
              {proofPhoto ? (
                <div className="relative rounded-md overflow-hidden border border-emerald-200">
                  <img
                    src={proofPhoto}
                    alt="Doorstep proof"
                    className="w-full h-40 object-cover"
                    data-testid="delivery-photo-preview"
                  />
                  <div className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] mono uppercase tracking-wider px-2 py-0.5 rounded shadow">
                    ✓ Photo captured
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={proofCompressing}
                  className="w-full h-32 border-2 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50/30 rounded-md flex flex-col items-center justify-center gap-1.5 text-slate-500 hover:text-emerald-700 transition-colors disabled:opacity-50"
                  data-testid="delivery-photo-capture"
                >
                  {proofCompressing ? (
                    <><Loader2 className="w-6 h-6 animate-spin" /><span className="text-xs">Processing…</span></>
                  ) : (
                    <>
                      <Camera className="w-7 h-7" />
                      <span className="text-xs font-medium">Snap doorstep photo</span>
                      <span className="text-[10px] text-slate-400">Required · opens camera</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="delivery-otp-value">4-digit code <span className="text-red-500">*</span></Label>
              <Input
                id="delivery-otp-value"
                inputMode="numeric"
                maxLength={4}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="0 0 0 0"
                className="text-center text-3xl mono tracking-[0.5em] h-14"
                data-testid="delivery-otp-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOtpOrder(null)}>Cancel</Button>
            <Button
              onClick={verifyOtp}
              disabled={busyId === otpOrder?.id || otp.length !== 4 || !proofPhoto}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="delivery-otp-submit"
            >
              {busyId === otpOrder?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Confirm delivered
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
